import SegmentLoadingButton from 'ui-component/Segment/SegmentLoadingButton';
import { GridLegacy } from '@mui/material';
import { useSelector } from 'store';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { FieldValues, useForm } from 'react-hook-form';

import { useGetOrgQuery, useUpdateOrgMutation } from 'store/slices/apiV1/org';
import { HookFormToggleCardButton } from 'ui-component/HookFormComponents';
import SegmentButton from 'ui-component/Segment/SegmentButton';

import { useEffect, useState } from 'react';
import { handleErr } from 'utils/functions';
import CardSectionWithAction from 'ui-component/cards/CardSectionWithAction';
import { ALL_APPS, ALL_FEATURE_IDS } from 'constants/appConstants';
import { appPermissionAccessLevels } from 'types/apps';
import { gridSpacing } from 'constants/themeConstants';
import { useAppAccessContext } from 'contexts/AppAccessContext';
import useSnackbar from 'hooks/useSnackbar';

const PartPreferences = () => {
  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();

  const { activeOrgId } = useSelector((state) => state.org);
  const { isPermissionDisabled } = useAppAccessContext();
  const customPnsEnabled = !isPermissionDisabled({
    app: ALL_APPS.PART_LIBRARY.id,
    feature: ALL_FEATURE_IDS.CUSTOM_PNS,
  });

  const { data: org, isLoading: isLoadingOrg } = useGetOrgQuery(
    activeOrgId ?? skipToken
  );
  const [updateOrg, { isLoading: isUpdatingOrg }] = useUpdateOrgMutation();
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = useForm<FieldValues>({
    defaultValues: {
      timezone: org?.timezone,
      bomMissingCustomIdWarning: org?.bomMissingCustomIdWarning,
      bomUploadCustomId: org?.bomUploadCustomId,
      bomClearCustomIdOnAltUnapprove: org?.bomClearCustomIdOnAltUnapprove,
      bomAddCustomIdOnAltApprove: org?.bomAddCustomIdOnAltApprove,
      bomTransferCustomIdOnEdit: org?.bomTransferCustomIdOnEdit,
      onlyOrgPartLibrary: org?.onlyOrgPartLibrary,
    },
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (org) {
      reset();
    }
  }, [isLoadingOrg]);

  const onSubmit = async (payload: FieldValues) => {
    if (isDirty) {
      try {
        await updateOrg({
          orgId: activeOrgId!,
          payload,
        }).unwrap();
        dispatchSuccessSnackbar('Preferences updated');
        setIsEditing(false);
      } catch (err) {
        handleErr(err, (errMessage: string) => {
          dispatchErrorSnackbar(errMessage);
        });
      }
    } else {
      setIsEditing(false);
    }
  };

  const actionButton = isEditing ? (
    <SegmentLoadingButton
      type="submit"
      variant="contained"
      color="primary"
      size="medium"
      loading={isUpdatingOrg}
      permissionScope={{
        app: ALL_APPS.ORG_SETTINGS.id,
        accessLevel: appPermissionAccessLevels.edit,
      }}
    >
      {isDirty ? 'Save' : 'Cancel'}
    </SegmentLoadingButton>
  ) : (
    <SegmentButton
      type="button"
      variant="contained"
      onClick={() => setIsEditing(true)}
      sx={{ float: 'right' }}
      permissionScope={{
        app: ALL_APPS.ORG_SETTINGS.id,
        accessLevel: appPermissionAccessLevels.edit,
      }}
    >
      Edit
    </SegmentButton>
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <GridLegacy container spacing={gridSpacing}>
        <GridLegacy item xs={12}>
          <CardSectionWithAction
            contents={
              <GridLegacy container spacing={gridSpacing}>
                <GridLegacy item xs={12}>
                  <HookFormToggleCardButton
                    name="onlyOrgPartLibrary"
                    control={control}
                    errors={errors}
                    disabled={!isEditing}
                    label={`Automatically Create Parts in ${org?.name}'s Library`}
                    options={[
                      {
                        value: false,
                        label: 'Enabled',
                        description: `Parts will automatically be created and updated in ${org?.name}'s Library via BOM Upload, DocAI, and Punchout. Use this setting if you want your library to dynamically update based on user activity in Cofactr.`,
                      },
                      {
                        value: true,
                        label: 'Disabled',
                        description: `Parts will not automatically be created or updated in ${org?.name}'s Library via BOM Upload, DocAI, and Punchout. Parts will have to be explicitly added to the library manually or via integrations. Use this setting if you want strict control of your library.`,
                      },
                    ]}
                  />
                </GridLegacy>
              </GridLegacy>
            }
            action={actionButton}
            title="Part Library"
          />
        </GridLegacy>
        {customPnsEnabled && (
          <GridLegacy item xs={12}>
            <CardSectionWithAction
              contents={
                <GridLegacy container spacing={gridSpacing}>
                  <GridLegacy item xs={12}>
                    <HookFormToggleCardButton
                      name="bomMissingCustomIdWarning"
                      control={control}
                      errors={errors}
                      disabled={!isEditing}
                      label={`Warnings for Parts in a BOM without a ${
                        org?.name || 'Custom'
                      } Part Number`}
                      options={[
                        {
                          value: 'allowed',
                          label: 'None',
                          description: `${
                            org?.name || 'Custom'
                          } Part Numbers are not required, and no warnings will be shown`,
                        },
                        {
                          value: 'warning',
                          label: 'Warning',
                          description: `${
                            org?.name || 'Custom'
                          } Part Numbers are recommended, and a warnings will be shown if they are missing`,
                        },
                        {
                          value: 'error',
                          label: 'Error',
                          description: `${
                            org?.name || 'Custom'
                          } Part Numbers are required, and an error will be shown if they are missing`,
                        },
                      ]}
                    />
                  </GridLegacy>
                  <GridLegacy item xs={12}>
                    <HookFormToggleCardButton
                      name="bomUploadCustomId"
                      control={control}
                      errors={errors}
                      disabled={!isEditing}
                      label={`Setting ${
                        org?.name || 'Custom'
                      } Part Numbers During BOM Uploads`}
                      options={[
                        {
                          value: 'none',
                          label: 'None',
                          description: `${
                            org?.name || 'Custom'
                          } Part Numbers included in BOM uploads will be used to help match parts in your library, but will not be set on parts that do not already have a ${
                            org?.name || 'Custom'
                          } Part Number set in Cofactr. This is the recommended setting if ${
                            org?.name || 'Custom'
                          } Part Numbers are uploaded to Cofactr via an integration or otherwise centrally managed.`,
                        },
                        {
                          value: 'primary',
                          label: 'Primary Part Only',
                          description: `${
                            org?.name || 'Custom'
                          } Part Numbers included in BOM uploads will be used to help match parts in your library and will be saved as ${
                            org?.name || 'Custom'
                          } Part Number on the first/primary part matched for each line`,
                        },
                        {
                          value: 'different_punctuation',
                          label: 'Primary Part + Alts (Picky Matching)',
                          description: `${
                            org?.name || 'Custom'
                          } Part Numbers included in BOM uploads will be used to help match parts in your library and will be saved as ${
                            org?.name || 'Custom'
                          } Part Number on the first/primary part matched for each line and any alt part that has the same part number as the primary part other than differences in punctuation`,
                        },
                        {
                          value: 'alts',
                          label: 'Primary Part + Alts',
                          description: `${
                            org?.name || 'Custom'
                          } Part Numbers included in BOM uploads will be used to help match parts in your library and will be saved as ${
                            org?.name || 'Custom'
                          } Part Number on the first/primary part matched for each line and all matched alt parts`,
                        },
                      ]}
                    />
                  </GridLegacy>
                  <GridLegacy item xs={12}>
                    <HookFormToggleCardButton
                      name="bomTransferCustomIdOnEdit"
                      control={control}
                      errors={errors}
                      disabled={!isEditing}
                      label={`Transfer ${
                        org?.name || 'Custom'
                      } Part Number When Changing BOM Line Part`}
                      options={[
                        {
                          value: false,
                          label: 'Disabled',
                          description: `When the first/primary part in a BOM is changed, if a ${
                            org?.name || 'Custom'
                          } Part Numbers was set on the first/primary part for the BOM line, it will remain set on the original part and will not be set on the newly selected part. This is the recommended setting if ${
                            org?.name || 'Custom'
                          } Part Numbers are uploaded to Cofactr via an integration or otherwise centrally managed.`,
                        },
                        {
                          value: true,
                          label: 'Enabled',
                          description: `When the first/primary part in a BOM is changed, if a ${
                            org?.name || 'Custom'
                          } Part Numbers was set on the first/primary part for the BOM line, it will be removed from the original part and will be set on the newly selected part. This change will be applied globally, so this is only recommended if ${
                            org?.name || 'Custom'
                          } Part Numbers are managed manually via BOM uploads in Cofactr.`,
                        },
                      ]}
                    />
                  </GridLegacy>
                  <GridLegacy item xs={12}>
                    <HookFormToggleCardButton
                      name="bomClearCustomIdOnAltUnapprove"
                      control={control}
                      errors={errors}
                      disabled={!isEditing}
                      label={`Clear ${
                        org?.name || 'Custom'
                      } Part Number When Unapproving Alt Parts`}
                      options={[
                        {
                          value: false,
                          label: 'Disabled',
                          description: `When an alt part in a BOM is unapproved, if a ${
                            org?.name || 'Custom'
                          } Part Numbers was set, it will remain applied to that part. This is the recommended setting if ${
                            org?.name || 'Custom'
                          } Part Numbers are uploaded to Cofactr via an integration or otherwise centrally managed.`,
                        },
                        {
                          value: true,
                          label: 'Enabled',
                          description: `When an alt part in a BOM is unapproved, if a ${
                            org?.name || 'Custom'
                          } Part Numbers was set, it will be removed from the part. This change will be applied globally, so this is only recommended if ${
                            org?.name || 'Custom'
                          } Part Numbers are managed manually via BOM uploads in Cofactr.`,
                        },
                      ]}
                    />
                  </GridLegacy>
                  <GridLegacy item xs={12}>
                    <HookFormToggleCardButton
                      name="bomAddCustomIdOnAltApprove"
                      control={control}
                      errors={errors}
                      disabled={!isEditing}
                      label={`Add ${
                        org?.name || 'Custom'
                      } Part Number When Approving Alt Parts`}
                      options={[
                        {
                          value: false,
                          label: 'Disabled',
                          description: `When an alt part in a BOM is approved, if a ${
                            org?.name || 'Custom'
                          } Part Numbers was set on the first/primary part for the BOM line, it will not be applied to the newly approved alt part. This is the recommended setting if ${
                            org?.name || 'Custom'
                          } Part Numbers are uploaded to Cofactr via an integration or otherwise centrally managed.`,
                        },
                        {
                          value: true,
                          label: 'Enabled',
                          description: `When an alt part in a BOM is approved, if a ${
                            org?.name || 'Custom'
                          } Part Numbers was set on the first/primary part for the BOM line, it will be applied to the newly approved alt part too. This change will be applied globally, so this is only recommended if ${
                            org?.name || 'Custom'
                          } Part Numbers are managed manually via BOM uploads in Cofactr.`,
                        },
                      ]}
                    />
                  </GridLegacy>
                </GridLegacy>
              }
              action={actionButton}
              title={`${org?.name || 'Custom'} Part Numbers`}
            />
          </GridLegacy>
        )}
      </GridLegacy>
    </form>
  );
};

export default PartPreferences;
